import React, { useState, useMemo, useEffect } from 'react';
import type { UserGroup, Permission } from '../../../../types/userRole';
import type {
  GroupModulePermissionsState,
  ModulePermissions
} from '../../../../types/groupModulePermissions';
import { mockModules } from '../../../../data/mockModules';
import PermissionsHeader from './components/PermissionsHeader';
import PermissionsSearch from './components/PermissionsSearch';
import PermissionsTable from './components/PermissionsTable';
import PermissionsSummary from './components/PermissionsSummary';
import '../../../../styles/groupModulePermissions.css';

interface GroupModulePermissionsProps {
  selectedGroup: UserGroup | null;
  onBack: () => void;
  className?: string;
  mode?: 'edit' | 'create';
  onPermissionsComplete?: (permissions: Permission[]) => void;
}

const GroupModulePermissions: React.FC<GroupModulePermissionsProps> = ({
  selectedGroup,
  onBack,
  className = '',
  mode = 'edit',
  onPermissionsComplete
}) => {
  const [state, setState] = useState<GroupModulePermissionsState>({
    selectedGroup,
    modules: mode === 'create' ? mockModules.map(module => ({
      ...module,
      permissions: {
        create: false,
        read: false,
        update: false,
        delete: false
      }
    })) : mockModules,
    searchTerm: '',
    selectedCategory: 'All Categories',
    hasChanges: false
  });


  // Update selected group when prop changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      selectedGroup
    }));
  }, [selectedGroup]);

  // Filter modules based on search term
  const filteredModules = useMemo(() => {
    let filtered = state.modules;

    // Filter by search term
    if (state.searchTerm) {
      filtered = filtered.filter(module =>
        module.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        module.category.toLowerCase().includes(state.searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [state.modules, state.searchTerm]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = filteredModules.reduce((acc, module) => {
      if (module.permissions.create) acc.create++;
      if (module.permissions.read) acc.read++;
      if (module.permissions.update) acc.update++;
      if (module.permissions.delete) acc.delete++;
      return acc;
    }, { create: 0, read: 0, update: 0, delete: 0 });
    
    return stats;
  }, [filteredModules]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm
    }));
  };


  const handlePermissionChange = (moduleId: string, permission: keyof ModulePermissions, value: boolean) => {
    setState(prev => ({
      ...prev,
      modules: prev.modules.map(module =>
        module.id === moduleId
          ? {
              ...module,
              permissions: {
                ...module.permissions,
                [permission]: value
              }
            }
          : module
      ),
      hasChanges: true
    }));
  };

  const handleBulkPermissionChange = (permission: keyof ModulePermissions, value: boolean) => {
    setState(prev => ({
      ...prev,
      modules: prev.modules.map(module => ({
        ...module,
        permissions: {
          ...module.permissions,
          [permission]: value
        }
      })),
      hasChanges: true
    }));
  };


  const handleSave = () => {
    console.log('GroupModulePermissions: handleSave called', { mode, hasCallback: !!onPermissionsComplete });
    
    if (mode === 'create' && onPermissionsComplete) {
      // Convert modules to permissions format for create mode
      const permissions: Permission[] = state.modules
        .filter(module =>
          module.permissions.create ||
          module.permissions.read ||
          module.permissions.update ||
          module.permissions.delete
        )
        .map(module => ({
          id: module.id,
          name: module.name,
          type: module.category === 'Core' ? 'full-access' : 'policy-management' as const,
          color: module.category === 'Core' ? '#3B82F6' : '#10B981' // Example colors based on category
        }));
      
      console.log('GroupModulePermissions: Calling onPermissionsComplete with permissions:', permissions);
      
      // Call the completion handler - this will add the group and show success modal
      onPermissionsComplete(permissions);
      
      // 🔥 FIX: Navigate back immediately after calling onPermissionsComplete
      console.log('GroupModulePermissions: Navigating back after permissions complete');
      onBack();
      
    } else {
      // In a real application, this would save to the backend
      console.log('Saving permissions for group:', state.selectedGroup?.name);
      console.log('Updated modules:', state.modules);
      
      setState(prev => ({
        ...prev,
        hasChanges: false
      }));

      // Show success message for edit mode
      alert(`Permissions saved successfully for ${state.selectedGroup?.name}!`);
    }
  };

  const handleBack = () => {
    if (state.hasChanges) {
      const confirmLeave = confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) {
        return;
      }
    }
    onBack();
  };

  return (
    <div className={`group-module-permissions ${className}`}>
      <PermissionsHeader
        selectedGroup={state.selectedGroup}
        hasChanges={state.hasChanges}
        onBack={handleBack}
        onSave={handleSave}
        mode={mode}
      />

      <PermissionsSearch
        searchTerm={state.searchTerm}
        moduleCount={filteredModules.length}
        onSearchChange={handleSearchChange}
      />

      <PermissionsTable
        modules={filteredModules}
        onPermissionChange={handlePermissionChange}
        onBulkPermissionChange={handleBulkPermissionChange}
      />

      <PermissionsSummary stats={summaryStats} totalModules={state.modules.length} />

    </div>
  );
};

export default GroupModulePermissions;